/** The central host's pass to a workspace address (ADR-0098 §4), which checks its own `to`. */
const HANDOVER = "/api/auth/handover";

/**
 * A path on this host to return to after signing in: one inside the app, or the pass to a
 * workspace address (no other server path); anything else is ignored.
 */
export function safeNext(next: unknown): string | null {
  if (typeof next !== "string" || !next.startsWith("/") || next.startsWith("//")) return null;
  // eslint-disable-next-line no-control-regex
  if (/[\\\u0000-\u001f\u007f]/.test(next)) return null;
  const path = next.split(/[?#]/)[0] ?? "";
  if (path === "/api" || path.startsWith("/api/")) {
    return path === HANDOVER && next.startsWith(`${HANDOVER}?`) ? next : null;
  }
  return next;
}

/** Whether a safe `next` is a page of the server's (the pass), loaded as a whole page. */
export const opensServerPage = (next: string) => next.startsWith(`${HANDOVER}?`);

/** The workspace address a pass in `next` is for ("latin.example.org"), if it is one. */
export function handoverHost(next: unknown): string | null {
  const safe = safeNext(next);
  if (!safe || !opensServerPage(safe)) return null;
  try {
    const to = new URLSearchParams(safe.slice(safe.indexOf("?") + 1)).get("to");
    return to ? new URL(to).host : null;
  } catch {
    return null;
  }
}

/** Where to go after signing in: `next` if safe, else home; the pass as a whole page. */
export function afterSignIn(next: unknown): { href: string; reloadDocument: boolean } {
  const href = safeNext(next) ?? "/";
  return { href, reloadDocument: opensServerPage(href) };
}
